import { rgba } from 'polished';
import { clamp } from 'lodash';
import { Bounds, Vector } from '@ardoq/graph';
import { ArrowType } from '@ardoq/api-types';
import { currentTimestamp } from '@ardoq/date-time';
import { BlocksViewLink, BlocksViewVisualState } from '../types';
import {
  BLOCKS_VIEW_LINK_MARKER_SIZE,
  BLOCKS_VIEW_HIGHLIGHT_COLOR,
  BLOCKS_VIEW_LINK_LABEL_MAXLENGTH,
  BLOCKS_VIEW_LINK_LABEL_PADDING,
  BLOCKS_VIEW_LINK_LABEL_HEIGHT,
  BLOCKS_VIEW_LINK_LABEL_COLOR,
  BLOCKS_VIEW_LINK_LABEL_FILLCOLOR,
  BLOCKS_VIEW_LINK_CORNER_RADIUS,
  BLOCKS_VIEW_LINK_HOVER_BLOATRATIO,
  BLOCKS_VIEW_ROUTEDCHANNEL_MINIMUM_SIZE,
  BLOCKS_VIEW_LOWLIGHT_ALPHA,
} from '../consts';
import { CanvasRenderingContext } from './types';
import {
  markerShapes,
  paintMarkerShapes,
  traceMarkerShapes,
  type MarkerPlacement,
} from 'tabview/canvasRendering/markerShapes';
import {
  rectCenter,
  rectContains,
  rectEnclose,
  rectHeight,
  rectIntersects,
  rectWidth,
} from '../misc/geometry';
import { getHoverColor } from '../misc/color';
import { truncateText } from '@ardoq/graph-labeling';

export const setLinkRoute = (link: BlocksViewLink, route: Vector[]) => {
  if (link.route) {
    link.animation = {
      begin: currentTimestamp(),
      duration: 500,
      from: link.route,
    };
  }

  link.route = route;
  link.bounds = rectEnclose(...route);
};

export const isLinkHitVisible = (link: BlocksViewLink) => {
  return link.globalTypeId !== null;
};

export const hitTestLink = (
  link: BlocksViewLink,
  pos: Vector,
  slop: number
) => {
  if (!link.bounds || !link.route || !isLinkHitVisible(link)) {
    return false;
  }

  if (
    pos[0] < link.bounds[0] - slop ||
    pos[1] < link.bounds[1] - slop ||
    pos[0] > link.bounds[2] + slop ||
    pos[1] > link.bounds[3] + slop
  ) {
    return false;
  }

  const hitTestSegment = (a: Vector, b: Vector) => {
    /* trivial exclusion */
    if (pos[0] < Math.min(a[0], b[0]) - slop) return false;
    if (pos[0] > Math.max(a[0], b[0]) + slop) return false;
    if (pos[1] < Math.min(a[1], b[1]) - slop) return false;
    if (pos[1] > Math.max(a[1], b[1]) + slop) return false;

    if (Math.abs(a[0] - b[0]) < 1 || Math.abs(a[1] - b[1]) < 1) {
      /* not trivially excluded from a manhattan segment is an inclusion */
      return true;
    }

    /* pull out the big guns for non manhattan segment */
    const sqr = (a: number) => a * a;
    const dist2 = (v: Vector, w: Vector) => sqr(v[0] - w[0]) + sqr(v[1] - w[1]);

    const l2 = dist2(a, b);
    if (l2 === 0) {
      return dist2(pos, a); // distance to end of zero length segment
    }

    const t = clamp(
      ((pos[0] - a[0]) * (b[0] - a[0]) + (pos[1] - a[1]) * (b[1] - a[1])) / l2,
      0,
      1
    );
    const d2 = dist2(pos, [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]);

    return d2 < slop || d2 < sqr(slop);
  };

  let a = link.route[0];

  for (let i = 1; i < link.route.length; ++i) {
    const b = link.route[i];

    if (hitTestSegment(a, b)) {
      return true;
    }

    a = b;
  }

  return false;
};

/**
 * Where the marker at `end` goes, facing along `prev → end`. `null` when it would be too
 * small to tell from the line itself.
 */
const markerPlacement = (
  prev: Vector,
  end: Vector,
  size: number,
  arrowType: ArrowType,
  context: CanvasRenderingContext
): MarkerPlacement | null =>
  arrowType === ArrowType.NONE || size < context.lineWidth
    ? null
    : {
        anchor: end,
        angle: Math.atan2(end[1] - prev[1], end[0] - prev[0]),
        size,
      };

/** Strokes the marker in the caller's current style, for the highlight halo. */
export const strokeMarker = (
  prev: Vector,
  end: Vector,
  size: number,
  arrowType: ArrowType,
  context: CanvasRenderingContext
) => {
  const placement = markerPlacement(prev, end, size, arrowType, context);
  const shapes = placement ? markerShapes(arrowType) : [];

  if (!placement || shapes.length === 0) {
    return;
  }

  context.save();
  context.setLineDash([]);
  context.beginPath();
  traceMarkerShapes(context, shapes, placement);
  context.stroke();
  context.restore();
};

export const drawMarker = (
  prev: Vector,
  end: Vector,
  size: number,
  arrowType: ArrowType,
  context: CanvasRenderingContext
) => {
  const placement = markerPlacement(prev, end, size, arrowType, context);

  if (!placement) {
    return;
  }

  context.save();
  context.setLineDash([]);
  paintMarkerShapes(context, markerShapes(arrowType), placement);
  context.restore();
};

const path = (
  route: Vector[],
  lineWidth: number,
  ctx: CanvasRenderingContext
) => {
  const square = () => {
    ctx.moveTo(...route[0]);

    for (let i = 1; i < route.length; ++i) {
      ctx.lineTo(route[i][0], route[i][1]);
    }
  };

  const mitred = () => {
    ctx.moveTo(...route[0]);

    let j = 0;

    for (let i = 1; i < route.length; ++i) {
      const v = [route[i][0] - route[j][0], route[i][1] - route[j][1]];

      if (v[0] === 0 && v[1] === 0) {
        continue;
      }

      const len = Math.hypot(v[0], v[1]);
      const r = Math.min(len / 3, BLOCKS_VIEW_LINK_CORNER_RADIUS);
      v[0] = (r * v[0]) / len;
      v[1] = (r * v[1]) / len;

      ctx.lineTo(route[j][0] + v[0], route[j][1] + v[1]);
      ctx.lineTo(route[i][0] - v[0], route[i][1] - v[1]);
      j = i;
    }

    ctx.lineTo(...route[route.length - 1]);
  };

  const rounded = () => {
    ctx.moveTo(...route[0]);

    let i0 = 0;
    let r0 = 0;
    const limit = 0.25;

    for (let i = 1; i < route.length; ++i) {
      const v = [route[i][0] - route[i0][0], route[i][1] - route[i0][1]];

      if (v[0] === 0 && v[1] === 0) {
        continue;
      }

      const len = Math.hypot(v[0], v[1]);
      const r = Math.min(len / 3, BLOCKS_VIEW_LINK_CORNER_RADIUS);

      v[0] = (r * v[0]) / len;
      v[1] = (r * v[1]) / len;

      if (r < limit) {
        ctx.lineTo(route[i0][0] + v[0], route[i0][1] + v[1]);
      } else {
        ctx.arcTo(
          route[i0][0],
          route[i0][1],
          route[i0][0] + v[0],
          route[i0][1] + v[1],
          Math.min(r, r0)
        );
      }

      ctx.lineTo(route[i][0] - v[0], route[i][1] - v[1]);
      i0 = i;
      r0 = r;
    }

    ctx.lineTo(...route[route.length - 1]);
  };

  if (BLOCKS_VIEW_LINK_CORNER_RADIUS < lineWidth) {
    square();
    return;
  }

  if (BLOCKS_VIEW_LINK_CORNER_RADIUS < 4 * lineWidth) {
    mitred();
    return;
  }

  rounded();
};

export const getLinkMinimumLabelWidth = (_link: BlocksViewLink): number => {
  /* this should be based on display label and also bounded */
  return BLOCKS_VIEW_ROUTEDCHANNEL_MINIMUM_SIZE;
};

export const getLinkMinimumLabelHeight = (
  _link: BlocksViewLink,
  _width: number
): number => {
  /* this will end up being required but currently only exists by symmetry with
  getLinkMinimumLabelWidth and the equivalent getNodeMinimum* functions */
  return (
    BLOCKS_VIEW_LINK_LABEL_PADDING +
    BLOCKS_VIEW_LINK_LABEL_HEIGHT +
    BLOCKS_VIEW_LINK_LABEL_PADDING
  );
};

export const getLinkLabelRect = (
  link: BlocksViewLink,
  window: Bounds,
  ctx: CanvasRenderingContext
): Bounds | null => {
  const route = link.route;
  const label = link.labels[0];

  if (!route || label === '') {
    return null;
  }

  let i = NaN;
  let lenMax = -Infinity;

  for (let j = 1; j < route.length; ++j) {
    const a = route[j - 1];
    const b = route[j];
    const len = Math.hypot(a[0] - b[0], a[1] - b[1]);

    if (len > lenMax) {
      lenMax = len;
      i = j - 1;
    }
  }

  if (!isFinite(i)) {
    return null;
  }

  const a = route[i];
  const b = route[i + 1];
  const c: Vector = [0.5 * (a[0] + b[0]), 0.5 * (a[1] + b[1])];

  if (!rectContains(window, c)) {
    return null;
  }

  const lenSegment = Math.hypot(a[0] - b[0], a[1] - b[1]);
  const lenLabel = Math.min(
    BLOCKS_VIEW_LINK_LABEL_MAXLENGTH,
    BLOCKS_VIEW_LINK_CORNER_RADIUS +
      BLOCKS_VIEW_LINK_LABEL_PADDING +
      ctx.measureText(link.labels[0]).width +
      BLOCKS_VIEW_LINK_LABEL_PADDING +
      BLOCKS_VIEW_LINK_CORNER_RADIUS
  );

  if (lenLabel > lenSegment) {
    return null;
  }

  const halfHeight =
    0.5 * BLOCKS_VIEW_LINK_LABEL_HEIGHT + BLOCKS_VIEW_LINK_LABEL_PADDING;
  const halfWidth = 0.5 * Math.min(lenLabel, lenSegment);

  const isVertical = Math.abs(a[0] - b[0]) < Math.abs(a[1] - b[1]);
  const rect: Bounds = isVertical
    ? [c[0] - halfHeight, c[1] - halfWidth, c[0] + halfHeight, c[1] + halfWidth]
    : [
        c[0] - halfWidth,
        c[1] - halfHeight,
        c[0] + halfWidth,
        c[1] + halfHeight,
      ];

  if (!rectIntersects(window, rect)) {
    return null;
  }

  return rect;
};

export const drawLink = (
  link: BlocksViewLink,
  route: Vector[],
  isLowlight: boolean,
  ctx: CanvasRenderingContext
) => {
  if (!route || route.length < 2) {
    return;
  }

  const isHover = link.visualState & BlocksViewVisualState.Hover;
  // const isSelect = link.visualState & BlocksViewVisualState.Select;
  const isHighlight = link.visualState & BlocksViewVisualState.Highlight;

  const ctxLineWidth = ctx.lineWidth;
  const lineWidth = ctx.lineWidth;
  const markerSize = BLOCKS_VIEW_LINK_MARKER_SIZE * (isHover ? 1.5 : 1);

  /* possibly draw the highlight */

  const globalAlpha = isLowlight
    ? isHighlight
      ? 1
      : BLOCKS_VIEW_LOWLIGHT_ALPHA
    : 1;
  ctx.globalAlpha = globalAlpha;

  if (!isLowlight && isHighlight) {
    ctx.beginPath();
    path(route, ctxLineWidth, ctx);

    ctx.lineWidth = 4 * lineWidth;
    ctx.strokeStyle = BLOCKS_VIEW_HIGHLIGHT_COLOR;
    ctx.setLineDash(link.dashArray);
    ctx.stroke();

    ctx.lineWidth = 4 * lineWidth;
    ctx.strokeStyle = BLOCKS_VIEW_HIGHLIGHT_COLOR;
    ctx.setLineDash([]);
    strokeMarker(route[1], route[0], 4 * markerSize, link.lineBeginning, ctx);
    strokeMarker(
      route[route.length - 2],
      route[route.length - 1],
      4 * markerSize,
      link.lineEnding,
      ctx
    );
  }

  /* draw the line */

  /* The markers above own their paths, and `save`/`restore` does not carry one back, so the
     route is built here rather than shared with the highlight. */
  ctx.beginPath();
  path(route, ctxLineWidth, ctx);

  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = link.color;
  ctx.setLineDash(link.dashArray);
  ctx.stroke();

  if (isHover) {
    ctx.lineWidth = BLOCKS_VIEW_LINK_HOVER_BLOATRATIO * lineWidth;
    ctx.strokeStyle = rgba(link.color, 0.35);
    ctx.stroke();
  }

  /* draw the markers */

  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = link.color;
  ctx.setLineDash([]);
  drawMarker(route[1], route[0], markerSize, link.lineBeginning, ctx);
  drawMarker(
    route[route.length - 2],
    route[route.length - 1],
    markerSize,
    link.lineEnding,
    ctx
  );

  ctx.lineWidth = ctxLineWidth;
};

export const drawLinkLabel = (
  link: BlocksViewLink,
  rect: Bounds,
  _isLowlight: boolean,
  ctx: CanvasRenderingContext
) => {
  const label = link.labels[0];
  const center = rectCenter(rect);
  const width = rectWidth(rect);
  const height = rectHeight(rect);
  const isVertical = width < height;
  const maxLen =
    (isVertical ? height : width) -
    2 * BLOCKS_VIEW_LINK_LABEL_PADDING -
    2 * BLOCKS_VIEW_LINK_CORNER_RADIUS;
  const truncate = truncateText((str: string) => ctx.measureText(str).width);
  const text = truncate(label, maxLen);
  const isHover = link.visualState & BlocksViewVisualState.Hover;
  const ctxLineWidth = ctx.lineWidth;

  if (text === '') {
    return;
  }

  /* background and border */

  ctx.fillStyle = isHover
    ? getHoverColor(BLOCKS_VIEW_LINK_LABEL_FILLCOLOR)
    : BLOCKS_VIEW_LINK_LABEL_FILLCOLOR;
  ctx.lineWidth = 0.75 * (isHover ? 2 : 1) * ctxLineWidth;
  ctx.strokeStyle = link.color;

  ctx.beginPath();
  ctx.roundRect(rect[0], rect[1], width, height, 0.5 * Math.min(width, height));
  ctx.fill();
  // ctx.stroke();

  /* text */

  ctx.fillStyle = BLOCKS_VIEW_LINK_LABEL_COLOR;

  if (isVertical) {
    ctx.save();
    ctx.translate(center[0], center[1]);
    ctx.rotate(0.5 * Math.PI);
    ctx.fillText(text, 0, 0);
    ctx.restore();
  } else {
    ctx.fillText(text, center[0], center[1]);
  }
};
